import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { wordpressApi } from '../../../api';
import type { WordPressPatchMode, WordPressSite, WordPressSiteSettings } from '../../../api';
import { ConfirmDialog, ErrorNotice, Field, LoadingRows } from '../../../components/ui';
import { FactList, LevelBadge, PanelCard, ProjectCol, ProjectGrid, ProjectSection, QuietEmpty, SectionHeader } from '../../../components/project/ui';
import { relativeTime } from '../../../components/project/projectHealth';
import { capabilityRows } from '../../../components/project/wordpressHealth';
import { WordPressSitePicker } from '../../../components/wordpress/WordPressSitePicker';
import type { WordPressTarget } from '../../../components/wordpress/WordPressSitePicker';
import { patchPolicyDescription, patchPolicyLabel } from '../../../labels';
import { useProject } from '../ProjectContext';
import { Checkbox, Input, Select } from '../../../components/primitives';

/**
 * WordPressSiteSection is where one environment's WordPress site lives:
 * connecting it, what inspection found, what Commerce Ops may do there and
 * the few operator actions that are safe to run by hand.
 */
export function WordPressSiteSection() {
  const project = useProject();
  const { environment, wordpressSite } = project;
  const [changing, setChanging] = useState(false);

  if (!environment) {
    return (
      <ProjectSection>
        <QuietEmpty testId="no-environment">
          No environments
          <Link className="button button--primary button--small quiet-empty__action" to={project.sectionPath('settings')}>
            Add an environment
          </Link>
        </QuietEmpty>
      </ProjectSection>
    );
  }

  if (wordpressSite.loading && !wordpressSite.data) {
    return (
      <ProjectSection>
        <PanelCard>
          <LoadingRows label="Loading the WordPress site" rows={4} rowHeight="28px" />
        </PanelCard>
      </ProjectSection>
    );
  }

  const site = wordpressSite.data;
  if (!site || changing) {
    return (
      <ProjectSection>
        <ConnectSiteCard
          site={site}
          onDone={() => {
            setChanging(false);
            project.refresh();
          }}
          onCancel={site ? () => setChanging(false) : undefined}
        />
      </ProjectSection>
    );
  }

  return (
    <ProjectSection>
      <SiteFactsCard site={site} onChange={() => setChanging(true)} />
      <ProjectGrid>
        <ProjectCol span={6}>
          <CapabilitiesCard site={site} />
        </ProjectCol>
        <ProjectCol span={6}>
          <ActionsCard site={site} />
        </ProjectCol>
      </ProjectGrid>
      <SettingsCard key={site.updated_at} site={site} />
    </ProjectSection>
  );
}

function ConnectSiteCard({
  site,
  onDone,
  onCancel,
}: {
  site: WordPressSite | null;
  onDone: () => void;
  onCancel?: () => void;
}) {
  const project = useProject();
  const [target, setTarget] = useState<WordPressTarget>({
    serverId: site?.server_id ?? '',
    rootPath: site?.root_path ?? '',
    releasesRoot: site?.releases_root ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  async function connect() {
    if (!project.environment) return;
    setBusy(true);
    setError(null);
    try {
      await wordpressApi.configureSite(project.projectId, project.environment.id, {
        server_id: target.serverId,
        root_path: target.rootPath,
        releases_root: target.releasesRoot || undefined,
      });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PanelCard labelledBy="wp-connect-title" testId="wp-connect">
      <SectionHeader id="wp-connect-title" title={site ? 'Change the WordPress site' : 'Connect the WordPress site'} />
      <WordPressSitePicker value={target} onChange={setTarget} />
      {site ? null : (
        <p className="panel__note" data-testid="wp-connect-plugin-alternative">
          No SSH or agent access? Use the Commerce Ops for WooCommerce plugin from{' '}
          <Link to={project.sectionPath('erp', { tab: 'stores' })}>ERP integrations ▸ Stores</Link>. Deployments,
          monitoring and backups need the site connected here.
        </p>
      )}
      <ErrorNotice error={error} />
      <div className="pill-list wp-section__actions">
        <button
          type="button"
          className="button button--primary"
          disabled={busy || !target.serverId || !target.rootPath.startsWith('/')}
          onClick={connect}
          data-testid="wp-connect-submit"
        >
          {busy ? 'Connecting and inspecting' : 'Connect and inspect'}
        </button>
        {onCancel ? (
          <button type="button" className="button button--secondary" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
        ) : null}
      </div>
    </PanelCard>
  );
}

function SiteFactsCard({ site, onChange }: { site: WordPressSite; onChange: () => void }) {
  const project = useProject();
  const [inspecting, setInspecting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const inspection = site.inspection;

  async function inspect() {
    setInspecting(true);
    setError(null);
    try {
      await wordpressApi.inspect(site.id);
      project.refresh();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setInspecting(false);
    }
  }

  const woo = inspection?.site.woocommerce;
  return (
    <PanelCard labelledBy="wp-site-title" testId="wp-site-facts">
      <SectionHeader
        id="wp-site-title"
        title={inspection?.site.blogname || 'WordPress site'}
        description={site.inspected_at ? `Inspected ${relativeTime(site.inspected_at)}` : 'Not inspected yet'}
        actions={
          <>
            <button type="button" className="button button--secondary" onClick={inspect} disabled={inspecting} data-testid="wp-inspect">
              {inspecting ? 'Inspecting' : 'Inspect again'}
            </button>
            <button type="button" className="button button--ghost" onClick={onChange}>
              Change site
            </button>
          </>
        }
      />
      <ErrorNotice error={error} />
      {site.inspection_error ? (
        <p className="panel__note panel__note--danger" role="status">
          The last inspection failed: {site.inspection_error}
        </p>
      ) : null}
      {inspection && !inspection.site.bootstrapped ? (
        <p className="panel__note panel__note--danger" role="status">
          WordPress only loads with its plugins skipped{inspection.site.bootstrap_error ? `: ${inspection.site.bootstrap_error}` : ''}
        </p>
      ) : null}
      <div className="wp-facts">
        <FactList
          facts={[
            { label: 'Address', value: inspection?.site.home ? <a href={inspection.site.home} target="_blank" rel="noreferrer">{inspection.site.home}</a> : <span className="muted">not known</span> },
            { label: 'WordPress', value: inspection?.site.wp_version || <span className="muted">not known</span> },
            { label: 'WooCommerce', value: woo ? `${woo.version}${woo.db_version && woo.db_version !== woo.version ? ` (database ${woo.db_version})` : ''}` : 'Not active' },
            { label: 'PHP', value: inspection?.site.php_version || <span className="muted">not known</span> },
            { label: 'Theme', value: inspection?.site.active_theme || <span className="muted">not known</span> },
            { label: 'Language', value: inspection?.site.locale || <span className="muted">not known</span> },
          ]}
        />
        <FactList
          facts={[
            { label: 'Server', value: <Link to={`/app/servers/${site.server_id}`}>{project.serverName(site.server_id)}</Link> },
            { label: 'Account', value: inspection?.host.user ? <code>{inspection.host.user}</code> : <span className="muted">not known</span> },
            { label: 'WordPress directory', value: <code>{site.root_path}</code> },
            { label: 'Releases and checkpoints', value: <code>{site.releases_root}</code> },
            { label: 'WP-CLI', value: inspection?.host.wp_cli_version || <span className="muted">not found</span> },
            {
              label: 'Environment type',
              value: inspection?.site.environment_type || <span className="muted">not set</span>,
            },
          ]}
        />
      </div>
      {(inspection?.warnings ?? []).length > 0 ? (
        <ul className="wp-warnings" aria-label="Inspection warnings">
          {inspection!.warnings!.map((warning) => (
            <li key={warning}>{warning}</li>
          ))}
        </ul>
      ) : null}
    </PanelCard>
  );
}

function CapabilitiesCard({ site }: { site: WordPressSite }) {
  const rows = capabilityRows(site);
  return (
    <PanelCard labelledBy="wp-capabilities-title" testId="wp-capabilities">
      <SectionHeader id="wp-capabilities-title" title="What the host allows" />
      {rows.length === 0 ? (
        <QuietEmpty>Not inspected yet</QuietEmpty>
      ) : (
        <ul className="status-list status-list--stacked wp-capabilities">
          {rows.map((row) => (
            <li key={row.key} className="status-list__item">
              <span className="status-list__name">{row.label}</span>
              <span className="status-list__detail">{row.value}</span>
              {row.neutral ? null : (
                <LevelBadge level={row.level}>
                  {row.level === 'ok' ? 'OK' : row.level === 'critical' ? 'Missing' : row.level === 'warning' ? 'Limited' : 'Not known'}
                </LevelBadge>
              )}
            </li>
          ))}
        </ul>
      )}
    </PanelCard>
  );
}

type PendingAction = 'flush' | 'maintenance' | null;

function ActionsCard({ site }: { site: WordPressSite }) {
  const project = useProject();
  const [pending, setPending] = useState<PendingAction>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [hook, setHook] = useState('');

  async function run(action: () => Promise<string>) {
    setBusy(true);
    setError(null);
    setDone(null);
    try {
      setDone(await action());
      project.refresh();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
      setPending(null);
    }
  }

  const hasActionScheduler = site.inspection?.capabilities.action_scheduler ?? false;

  return (
    <PanelCard labelledBy="wp-actions-title" testId="wp-actions">
      <SectionHeader id="wp-actions-title" title="Safe actions" />
      <ul className="wp-actions">
        <li>
          <div>
            <strong>Clear caches</strong>
          </div>
          <button type="button" className="button button--secondary" onClick={() => setPending('flush')} disabled={busy}>
            Clear caches
          </button>
        </li>
        <li>
          <div>
            <strong>Turn off maintenance mode</strong>
          </div>
          <button type="button" className="button button--secondary" onClick={() => setPending('maintenance')} disabled={busy}>
            Turn off
          </button>
        </li>
        {hasActionScheduler ? (
          <li className="wp-actions__form">
            <div>
              <strong>Retry failed scheduled actions</strong>
              <p className="muted">Hooks that take a payment, renew a subscription or change an order are refused.</p>
            </div>
            <div className="wp-actions__inline">
              <label className="visually-hidden" htmlFor="wp-retry-hook">
                Hook name
              </label>
              <Input
                id="wp-retry-hook"
                value={hook}
                onChange={(event) => setHook(event.target.value.trim())}
                placeholder="woocommerce_run_product_attribute_lookup_update_callback"
                spellCheck={false}
              />
              <button
                type="button"
                className="button button--secondary"
                disabled={busy || hook === ''}
                onClick={() =>
                  run(async () => {
                    const res = await wordpressApi.retryScheduledActions(site.id, hook);
                    return `${res.requeued} ${res.requeued === 1 ? 'action' : 'actions'} queued again.`;
                  })
                }
              >
                Retry
              </button>
            </div>
          </li>
        ) : null}
      </ul>
      <ErrorNotice error={error} />
      {done ? (
        <p className="panel__note" role="status">
          {done}
        </p>
      ) : null}

      <ConfirmDialog
        open={pending === 'flush'}
        title="Clear the caches?"
        description="The object cache and the detected page cache are emptied. The shop stays online; the first visits afterwards are slower while the caches refill."
        confirmLabel="Clear caches"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() =>
          run(async () => {
            await wordpressApi.flushCaches(site.id);
            return 'Caches cleared.';
          })
        }
      />
      <ConfirmDialog
        open={pending === 'maintenance'}
        title="Turn off maintenance mode?"
        description="Removes WordPress's maintenance flag so visitors see the shop again. If an update is genuinely still running, finish or retry it first."
        confirmLabel="Turn off maintenance mode"
        busy={busy}
        onCancel={() => setPending(null)}
        onConfirm={() =>
          run(async () => {
            await wordpressApi.disableMaintenance(site.id);
            return 'Maintenance mode is off.';
          })
        }
      />
    </PanelCard>
  );
}

const PATCH_MODES: WordPressPatchMode[] = ['manual_approval', 'security_only', 'minor_and_security', 'allowlist'];

function minutes(seconds: number | undefined): string {
  return seconds ? String(Math.round(seconds / 60)) : '';
}

function SettingsCard({ site }: { site: WordPressSite }) {
  const project = useProject();
  const s = site.settings;
  const [productUrl, setProductUrl] = useState(s.product_url ?? '');
  const [categoryUrl, setCategoryUrl] = useState(s.category_url ?? '');
  const [mode, setMode] = useState<WordPressPatchMode>((s.patch_policy?.mode || 'manual_approval') as WordPressPatchMode);
  const [allowlist, setAllowlist] = useState((s.patch_policy?.allowlist ?? []).join(', '));
  const [cronWarn, setCronWarn] = useState(minutes(s.cron?.warn_late_seconds));
  const [cronCritical, setCronCritical] = useState(minutes(s.cron?.critical_late_seconds));
  const [latencyWarn, setLatencyWarn] = useState(s.latency_warning_ms ? String(s.latency_warning_ms) : '');
  const [keepReleases, setKeepReleases] = useState(s.keep_releases ? String(s.keep_releases) : '');
  const [runBuilds, setRunBuilds] = useState(s.run_builds !== false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [saved, setSaved] = useState(false);

  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setSaved(false);
    // Start from what is stored so fields this form does not show (the
    // deployable units, Action Scheduler thresholds) are kept as they are.
    const next: WordPressSiteSettings = {
      ...s,
      product_url: productUrl.trim() || undefined,
      category_url: categoryUrl.trim() || undefined,
      run_builds: runBuilds,
      latency_warning_ms: Number(latencyWarn) || undefined,
      keep_releases: Number(keepReleases) || undefined,
      cron: {
        ...s.cron,
        warn_late_seconds: Number(cronWarn) > 0 ? Number(cronWarn) * 60 : 0,
        critical_late_seconds: Number(cronCritical) > 0 ? Number(cronCritical) * 60 : 0,
      },
      patch_policy: {
        ...s.patch_policy,
        mode,
        allowlist:
          mode === 'allowlist'
            ? allowlist
                .split(/[\s,]+/)
                .map((slug) => slug.trim())
                .filter(Boolean)
            : s.patch_policy?.allowlist,
      },
    };
    try {
      await wordpressApi.updateSettings(site.id, next);
      setSaved(true);
      project.refresh();
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setBusy(false);
    }
  }

  return (
    <PanelCard labelledBy="wp-settings-title" testId="wp-settings">
      <SectionHeader id="wp-settings-title" title="Checks and update policy" />
      <form className="form wp-settings" onSubmit={save}>
        <div className="wp-picker__row">
          <Field label="Product page to check" hint="Optional. Without one, no product page is checked.">
            <Input value={productUrl} onChange={(event) => setProductUrl(event.target.value)} placeholder="https://shop.example.com/product/mug/" spellCheck={false} />
          </Field>
          <Field label="Category page to check" hint="Optional. Without one, no category page is checked.">
            <Input value={categoryUrl} onChange={(event) => setCategoryUrl(event.target.value)} placeholder="https://shop.example.com/product-category/mugs/" spellCheck={false} />
          </Field>
        </div>
        <Field label="Update policy" hint={patchPolicyDescription[mode]}>
          <Select value={mode} onChange={(event) => setMode(event.target.value as WordPressPatchMode)} data-testid="wp-patch-policy">
            {PATCH_MODES.map((value) => (
              <option key={value} value={value}>
                {patchPolicyLabel[value]}
              </option>
            ))}
          </Select>
        </Field>
        {mode === 'allowlist' ? (
          <Field label="Plugins and themes the policy covers" hint="Slugs, separated by commas. Major updates still need approval.">
            <Input value={allowlist} onChange={(event) => setAllowlist(event.target.value)} placeholder="contact-form-7, storefront" spellCheck={false} />
          </Field>
        ) : null}
        <p className="panel__note muted">Major updates, unknown sources and declared incompatibilities always need approval.</p>
        <div className="wp-picker__row">
          <Field label="WP-Cron late: warn after (minutes)">
            <Input value={cronWarn} onChange={(event) => setCronWarn(event.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="30" />
          </Field>
          <Field label="WP-Cron late: critical after (minutes)">
            <Input value={cronCritical} onChange={(event) => setCronCritical(event.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="180" />
          </Field>
        </div>
        <div className="wp-picker__row">
          <Field label="Slow page warning (ms)">
            <Input value={latencyWarn} onChange={(event) => setLatencyWarn(event.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="3000" />
          </Field>
          <Field label="Releases kept for rollback">
            <Input value={keepReleases} onChange={(event) => setKeepReleases(event.target.value.replace(/[^0-9]/g, ''))} inputMode="numeric" placeholder="5" />
          </Field>
        </div>
        <Checkbox
          checked={runBuilds}
          onChange={(event) => setRunBuilds(event.target.checked)}
          label={
            <>
              Run Composer and Node builds on the server during a deployment
              <span className="muted"> — turn off when the host has too little memory, and commit built assets instead.</span>
            </>
          }
        />
        <ErrorNotice error={error} />
        {saved ? (
          <p className="panel__note" role="status">
            Saved
          </p>
        ) : null}
        <div>
          <button type="submit" className="button button--primary" disabled={busy}>
            {busy ? 'Saving' : 'Save'}
          </button>
        </div>
      </form>
    </PanelCard>
  );
}
